import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Zap, HardHat, History, ChevronRight, Loader2, Clock, AlertTriangle, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { prospectsAPI } from '@/lib/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { MarkAsLost, LostBadge } from './MarkAsLost';

// Étapes bloquées sans RGPD
const BLOCKED_STAGES_WITHOUT_RGPD = [
    'ep_etude_recue', 'ep_etude_expliquee', 'ep_rib_receptionne',
    'ep_contrat_envoye', 'ep_contrat_explique', 'ep_gagne', 'ep_questionnaire_chantier'
];

// Stages Courtage
const EP_STAGES = [
    { id: 'ep_debut_prospection', name: 'Début prospection', order: 1 },
    { id: 'ep_facture_recue', name: 'Facture reçue', order: 2 },
    { id: 'ep_mandat_envoye', name: 'Mandat envoyé', order: 3 },
    { id: 'ep_mandat_signe', name: 'Mandat signé', order: 4 },
    { id: 'ep_dossier_transmis', name: 'Dossier transmis', order: 5 },
    { id: 'ep_etude_recue', name: 'Étude reçue', order: 6 },
    { id: 'ep_etude_expliquee', name: 'Étude expliquée', order: 7 },
    { id: 'ep_rib_receptionne', name: 'RIB réceptionné', order: 8 },
    { id: 'ep_contrat_envoye', name: 'Contrat envoyé', order: 9 },
    { id: 'ep_contrat_explique', name: 'Attente signature', order: 10 },
    { id: 'ep_gagne', name: 'Gagné', order: 11 },
    { id: 'ep_questionnaire_chantier', name: 'Formulaire rempli', order: 12 },
    { id: 'ep_perdu', name: 'Perdu', order: -1, isLost: true },
];

// Stages Travaux d’optimisation
const CH_STAGES = [
    { id: 'ch_formulaire_rempli', name: 'Formulaire rempli', order: 1 },
    { id: 'ch_visite_planifiee', name: 'Visite planifiée', order: 2 },
    { id: 'ch_visite_effectuee', name: 'Visite effectuée', order: 3 },
    { id: 'ch_dossier_etude', name: 'À l\'étude', order: 4 },
    { id: 'ch_etude_presentee', name: 'Étude présentée', order: 5 },
    { id: 'ch_devis_envoye', name: 'Devis envoyé', order: 6 },
    { id: 'ch_devis_accepte', name: 'Devis accepté', order: 7 },
    { id: 'ch_planification', name: 'Planification', order: 8 },
    { id: 'ch_en_cours', name: 'En cours', order: 9 },
    { id: 'ch_termine', name: 'Terminé', order: 10 },
    { id: 'ch_attente_paiement', name: 'Attente paiement', order: 11 },
    { id: 'ch_paiement_effectue', name: 'Payé', order: 12 },
    { id: 'ch_valide', name: 'Validé', order: 13 },
    { id: 'ch_perdu', name: 'Perdu', order: -1, isLost: true },
];

function getStageName(stageId, pipelineType) {
    const stages = pipelineType === 'easy_power' ? EP_STAGES : CH_STAGES;
    const stage = stages.find(s => s.id === stageId);
    return stage?.name || stageId || 'Non défini';
}

function getStageOrder(stageId, pipelineType) {
    const stages = pipelineType === 'easy_power' ? EP_STAGES : CH_STAGES;
    const stage = stages.find(s => s.id === stageId);
    return stage?.order || 0;
}

export function StageChanger({ prospect, onStageChanged, pipelineType }) {
    const [changing, setChanging] = useState(false);
    const [selectedPipeline, setSelectedPipeline] = useState(null);
    const [selectedStage, setSelectedStage] = useState('');
    const [comment, setComment] = useState('');
    const [historyDialog, setHistoryDialog] = useState({ open: false, pipeline: null });
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    const hasEasyPower = !!prospect.easy_power_stage;
    const hasChantier = !!prospect.chantier_stage;
    
    // Si pipelineType est spécifié, on filtre pour n'afficher que ce pipeline
    const showEasyPower = pipelineType ? pipelineType === 'easy_power' && hasEasyPower : hasEasyPower;
    const showChantier = pipelineType ? pipelineType === 'chantier' && hasChantier : hasChantier;

    // Vérification RGPD
    const isRgpdRealise = prospect.rgpd_mandat?.realise === true;
    const isFactureRecueStage = prospect.easy_power_stage === 'ep_facture_recue';
    const showRgpdAlert = hasEasyPower && isFactureRecueStage && !isRgpdRealise;

    // Fonction pour vérifier si une étape est bloquée sans RGPD
    const isStageBlockedByRgpd = (stageId) => {
        if (isRgpdRealise) return false;
        return BLOCKED_STAGES_WITHOUT_RGPD.includes(stageId);
    };

    const handleChangeStage = async () => {
        if (!selectedPipeline || !selectedStage) return;
        
        // Vérification côté client avant envoi
        if (selectedPipeline === 'easy_power' && isStageBlockedByRgpd(selectedStage)) {
            toast.error('Mandat RGPD obligatoire avant de passer à cette étape');
            return;
        }
        
        setChanging(true);
        try {
            await prospectsAPI.changeStage(prospect.id, selectedPipeline, selectedStage, comment || null);
            toast.success('Etape mise a jour');
            setSelectedPipeline(null);
            setSelectedStage('');
            setComment('');
            onStageChanged?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors du changement d\'étape');
        } finally {
            setChanging(false);
        }
    };

    const loadHistory = async (pipelineType) => {
        setLoadingHistory(true);
        try {
            const res = await prospectsAPI.getStageHistory(prospect.id, pipelineType);
            setHistory(res.data.history || []);
        } catch (error) {
            console.error('Error loading history:', error);
            setHistory([]);
        } finally {
            setLoadingHistory(false);
        }
    };

    const openHistoryDialog = (pipelineType) => {
        setHistoryDialog({ open: true, pipeline: pipelineType });
        loadHistory(pipelineType);
    };

    return (
        <Card data-testid="stage-changer">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <ChevronRight className="w-5 h-5" />
                    Progression du dossier
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Alerte RGPD si à l'étape Facture reçue et non réalisé */}
                {showRgpdAlert && (
                    <Alert variant="destructive" className="border-red-400 bg-red-50" data-testid="rgpd-stage-alert">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription className="flex items-center gap-2">
                            <Shield className="w-4 h-4" />
                            <span><strong>Mandat RGPD obligatoire</strong> avant de passer à l'étude. Les étapes suivantes sont bloquées tant que le mandat n'est pas réalisé.</span>
                        </AlertDescription>
                    </Alert>
                )}

                {/* Courtage Stage */}
                {showEasyPower && (
                    <div className="p-4 rounded-lg border border-amber-200 bg-amber-50/50">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <Zap className="w-5 h-5 text-amber-600" />
                                <span className="font-medium text-amber-700">Courtage</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <MarkAsLost 
                                    prospect={prospect} 
                                    pipelineType="easy_power" 
                                    onLost={onStageChanged} 
                                />
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openHistoryDialog('easy_power')}
                                    data-testid="ep-history-btn"
                                >
                                    <History className="w-4 h-4 mr-1" />
                                    Historique
                                </Button>
                            </div>
                        </div>
                        
                        {/* Badge si perdu */}
                        <LostBadge prospect={prospect} pipelineType="easy_power" />
                        
                        <div className="flex items-center gap-3">
                            <Badge className="bg-amber-100 text-amber-700 border-amber-300">
                                {getStageName(prospect.easy_power_stage, 'easy_power')}
                            </Badge>
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                            <Select
                                value={selectedPipeline === 'easy_power' ? selectedStage : ''}
                                onValueChange={(val) => {
                                    setSelectedPipeline('easy_power');
                                    setSelectedStage(val);
                                }}
                            >
                                <SelectTrigger className="w-48" data-testid="ep-stage-select">
                                    <SelectValue placeholder="Changer l'étape..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {EP_STAGES.map((stage) => {
                                        const isBlocked = isStageBlockedByRgpd(stage.id);
                                        const isCurrent = stage.id === prospect.easy_power_stage;
                                        const isLost = stage.isLost;
                                        return (
                                            <SelectItem 
                                                key={stage.id} 
                                                value={stage.id}
                                                disabled={isCurrent || isBlocked || isLost}
                                                className={isBlocked ? 'text-red-400' : isLost ? 'text-red-400' : ''}
                                            >
                                                {stage.name}
                                                {isBlocked && ' (RGPD)'}
                                                {isLost && ' (utiliser bouton Perdu)'}
                                            </SelectItem>
                                        );
                                    })}
                                </SelectContent>
                            </Select>
                        </div>
                        {/* Progress bar */}
                        <div className="mt-3">
                            <div className="flex justify-between text-xs text-slate-500 mb-1">
                                <span>Progression</span>
                                <span>{Math.round((getStageOrder(prospect.easy_power_stage, 'easy_power') / EP_STAGES.length) * 100)}%</span>
                            </div>
                            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                                <div 
                                    className="h-full bg-amber-500 transition-all duration-300"
                                    style={{ width: `${(getStageOrder(prospect.easy_power_stage, 'easy_power') / EP_STAGES.length) * 100}%` }}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Travaux d’optimisation Stage */}
                {showChantier && (
                    <div className="p-4 rounded-lg border border-blue-200 bg-blue-50/50">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <HardHat className="w-5 h-5 text-blue-600" />
                                <span className="font-medium text-blue-700">Travaux d’optimisation</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <MarkAsLost 
                                    prospect={prospect} 
                                    pipelineType="chantier" 
                                    onLost={onStageChanged} 
                                />
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openHistoryDialog('chantier')}
                                    data-testid="ch-history-btn"
                                >
                                    <History className="w-4 h-4 mr-1" />
                                    Historique
                                </Button>
                            </div>
                        </div>
                        
                        {/* Badge si perdu */}
                        <LostBadge prospect={prospect} pipelineType="chantier" />
                        
                        <div className="flex items-center gap-3">
                            <Badge className="bg-blue-100 text-blue-700 border-blue-300">
                                {getStageName(prospect.chantier_stage, 'chantier')}
                            </Badge>
                            <ChevronRight className="w-4 h-4 text-slate-400" />
                            <Select
                                value={selectedPipeline === 'chantier' ? selectedStage : ''}
                                onValueChange={(val) => {
                                    setSelectedPipeline('chantier');
                                    setSelectedStage(val);
                                }}
                            >
                                <SelectTrigger className="w-48" data-testid="ch-stage-select">
                                    <SelectValue placeholder="Changer l'étape..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {CH_STAGES.map((stage) => (
                                        <SelectItem 
                                            key={stage.id} 
                                            value={stage.id}
                                            disabled={stage.id === prospect.chantier_stage || stage.isLost}
                                            className={stage.isLost ? 'text-red-400' : ''}
                                        >
                                            {stage.name}
                                            {stage.isLost && ' (utiliser bouton Perdu)'}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        {/* Progress bar */}
                        <div className="mt-3">
                            <div className="flex justify-between text-xs text-slate-500 mb-1">
                                <span>Progression</span>
                                <span>{Math.round((getStageOrder(prospect.chantier_stage, 'chantier') / CH_STAGES.length) * 100)}%</span>
                            </div>
                            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                                <div 
                                    className="h-full bg-blue-500 transition-all duration-300"
                                    style={{ width: `${(getStageOrder(prospect.chantier_stage, 'chantier') / CH_STAGES.length) * 100}%` }}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Comment + Confirm button */}
                {selectedPipeline && selectedStage && (
                    <div className="p-4 border rounded-lg bg-slate-50 space-y-3">
                        <p className="text-sm text-slate-600">
                            Vous allez changer l'etape <strong>{selectedPipeline === 'easy_power' ? 'Courtage' : 'Travaux d’optimisation'}</strong> 
                            {' '}vers <Badge variant="outline">{getStageName(selectedStage, selectedPipeline)}</Badge>
                        </p>
                        <Textarea
                            placeholder="Commentaire (optionnel)..."
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            rows={2}
                            data-testid="stage-comment"
                        />
                        <div className="flex gap-2">
                            <Button
                                onClick={handleChangeStage}
                                disabled={changing}
                                data-testid="confirm-stage-btn"
                            >
                                {changing && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                                Confirmer le changement
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setSelectedPipeline(null);
                                    setSelectedStage('');
                                    setComment('');
                                }}
                            >
                                Annuler
                            </Button>
                        </div>
                    </div>
                )}

                {!showEasyPower && !showChantier && (
                    <p className="text-slate-500 text-center py-4">
                        {pipelineType 
                            ? `Pipeline ${pipelineType === 'easy_power' ? 'Courtage' : 'Travaux d’optimisation'} non activé pour ce prospect.`
                            : 'Aucun pipeline actif pour ce prospect.'
                        }
                    </p>
                )}
            </CardContent>

            {/* History Dialog */}
            <Dialog open={historyDialog.open} onOpenChange={(open) => setHistoryDialog({ open, pipeline: null })}>
                <DialogContent className="max-w-lg max-h-[70vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <History className="w-5 h-5" />
                            Historique des etapes - {historyDialog.pipeline === 'easy_power' ? 'Courtage' : 'Travaux d’optimisation'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {loadingHistory ? (
                        <div className="py-8 text-center">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                        </div>
                    ) : history.length === 0 ? (
                        <p className="text-slate-500 text-center py-8">Aucun historique disponible.</p>
                    ) : (
                        <div className="space-y-3">
                            {history.map((entry, idx) => (
                                <div 
                                    key={entry.id || idx} 
                                    className={cn(
                                        "p-3 rounded-lg border",
                                        idx === 0 ? "bg-green-50 border-green-200" : "bg-slate-50"
                                    )}
                                >
                                    <div className="flex items-center gap-2 text-sm">
                                        <Badge variant="outline" className="text-slate-500">
                                            {getStageName(entry.old_stage, historyDialog.pipeline)}
                                        </Badge>
                                        <ChevronRight className="w-4 h-4 text-slate-400" />
                                        <Badge className={idx === 0 ? "bg-green-100 text-green-700" : ""}>
                                            {getStageName(entry.new_stage, historyDialog.pipeline)}
                                        </Badge>
                                    </div>
                                    <div className="mt-2 text-xs text-slate-500 flex items-center gap-2">
                                        <Clock className="w-3 h-3" />
                                        {format(new Date(entry.changed_at), "dd MMM yyyy 'a' HH:mm", { locale: fr })}
                                        <span>par</span>
                                        <span className="font-medium">{entry.changed_by_name}</span>
                                    </div>
                                    {entry.comment && (
                                        <p className="mt-2 text-sm text-slate-600 italic">"{entry.comment}"</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </Card>
    );
}
